/**
 * Alert Component
 *
 * Multi-variant alert for success, warning, info, and error messages.
 * Includes appropriate icons and ARIA attributes for accessibility.
 *
 * ErrorAlert.jsx is a thin wrapper around this component for
 * backward compatibility with existing imports.
 *
 * SCRUM-902: Alert Component Multi-Variant Extension
 *
 * @component
 * @param {Object} props
 * @param {'error'|'success'|'warning'|'info'} props.variant - Alert variant
 * @param {string} props.message - Alert message text
 * @param {string} [props.className] - Additional classes
 * @param {React.ReactNode} [props.children] - Optional rich content (overrides message)
 */
import PropTypes from 'prop-types';
import { AlertCircle, CheckCircle, AlertTriangle, Info } from 'lucide-react';

const variantConfig = {
  error: {
    container: 'bg-red-50 border-red-200',
    icon: 'text-red-600',
    text: 'text-red-800',
    Icon: AlertCircle,
    ariaLive: 'assertive',
  },
  success: {
    container: 'bg-green-50 border-green-200',
    icon: 'text-green-600',
    text: 'text-green-800',
    Icon: CheckCircle,
    ariaLive: 'polite',
  },
  warning: {
    container: 'bg-yellow-50 border-yellow-200',
    icon: 'text-yellow-600',
    text: 'text-yellow-800',
    Icon: AlertTriangle,
    ariaLive: 'polite',
  },
  info: {
    container: 'bg-blue-50 border-blue-200',
    icon: 'text-blue-600',
    text: 'text-blue-800',
    Icon: Info,
    ariaLive: 'polite',
  },
};

export default function Alert({ variant = 'error', message, className = '', children }) {
  const config = variantConfig[variant] || variantConfig.error;
  const { Icon } = config;

  return (
    <div
      role="alert"
      aria-live={config.ariaLive}
      className={`border rounded-lg p-6 ${config.container} ${className}`.trim()}
    >
      <div className="flex items-center space-x-2">
        <Icon className={`w-5 h-5 flex-shrink-0 ${config.icon}`} />
        <div className={config.text}>
          {children || <p>{message}</p>}
        </div>
      </div>
    </div>
  );
}

Alert.propTypes = {
  variant: PropTypes.oneOf(['error', 'success', 'warning', 'info']),
  message: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
};
